import { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Table,
  Badge,
} from "react-bootstrap";
import Select from "react-select";
import moment from "moment";
import { toast } from "react-toastify";

import {
  updateTicket,
  closeTicket,
  addTicketWorkLog,
  getAllTicketClosureTypes,
  getAllTicketLoggedTypes,
  getAllTicketStatuses,
  getTicketWorkDetails,
} from "../Api/TicketApiOrg";
import { ticketDropdownApi } from "../Api/TicketApiOrg";
import { fetchResourceById } from "../Api/ResourceMaster";
import { getResourceByMavUserId } from "../Api/ResourceList";
import { fetchCusVenCpById } from "../Api/CusVenCpMasterApi";
import { IoMdRefreshCircle } from "react-icons/io";
<IoMdRefreshCircle />;
const TicketEditModal = ({
  show,
  onClose,
  ticket,
  orgId,
  userDetails,
  projectOptions,
  fetchProjectAndPartners,
  refreshTickets,
  currentProject,
  isProjectReadOnly = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [showWorkLogModal, setShowWorkLogModal] = useState(false);
  const [ticketReasons, setTicketReasons] = useState([]);
  const [closureTypes, setClosureTypes] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // State for work logs
  const [workLogsData, setWorkLogsData] = useState(null);
  const [loadingWorkLogs, setLoadingWorkLogs] = useState(false);

  // State for user names cache
  const [userNamesCache, setUserNamesCache] = useState({});
  const [loadingUserNames, setLoadingUserNames] = useState(false);

  const [submittedByOptions, setSubmittedByOptions] = useState([]);
  const [loadingSubmittedBy, setLoadingSubmittedBy] = useState(false);

  // Projects by Submitter
  const [projectOptionsBySubmitter, setProjectOptionsBySubmitter] = useState(
    [],
  );
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Status Master Data
  const [statusOptions, setStatusOptions] = useState([]);
  const [subStatusMap, setSubStatusMap] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  // ========== SEPARATE LISTS FOR DIFFERENT PURPOSES ==========

  // 1. For "Assigned To" field - includes Team, Customer, Partner (with labels)
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [loadingAssignedTo, setLoadingAssignedTo] = useState(false);

  // 2. For "Submitted By" field - uses separate API
  // Already using submittedByOptions state above

  // 3. For "Work Log Worked By" field - NOW SAME AS ASSIGNED TO (Team, Customer, Partner)
  const [workLogUserOptions, setWorkLogUserOptions] = useState([]);
  const [loadingWorkLogUsers, setLoadingWorkLogUsers] = useState(false);

  const [typeOptions, setTypeOptions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [updateForm, setUpdateForm] = useState({
    ticketName: "",
    description: "",
    status: "",
    priority: "",
    assignedTo: [],
    submittedBy: "",
    category: "",
    dueDate: "",
    referenceTo: "",
    projectAssigned: "",
    ticketType: "",
    ticketReason: "",
    subStatus: "",
  });

  // Closure form state (inline)
  const [closureForm, setClosureForm] = useState({
    closureTypeCode: "",
    closureRemarks: "",
    efforts: [
      {
        workedBy: "",
        effortValue: "",
        effortUnit: "HOURS",
      },
    ],
  });
  const [isClosing, setIsClosing] = useState(false);

  const [workLogForm, setWorkLogForm] = useState({
    workedBy: "",
    effortValue: "",
    effortUnit: "HOURS",
    remarks: "",
  });

  // Helper to check if a string is a name (contains spaces and letters)
  const isNameString = (str) => {
    if (!str) return false;
    return (
      /^[A-Za-z\s]+$/.test(str) && !str.startsWith("CVCP") && isNaN(Number(str))
    );
  };

  // ========== FUNCTIONS FOR WORK LOG USERS (SAME AS ASSIGNED TO) ==========

  // Build work log user options (SAME AS ASSIGNED TO - Team, Customer, Partner)
  const buildWorkLogUserOptions = async () => {
    if (!updateForm.projectAssigned || !orgId) {
      setWorkLogUserOptions([]);
      return;
    }

    setLoadingWorkLogUsers(true);

    try {
      const projectIds = updateForm.projectAssigned
        .split(",")
        .map((id) => id.trim());

      const userMap = new Map();

      for (const pid of projectIds) {
        const [team, customers, partners] = await Promise.all([
          ticketDropdownApi.getTeamAssigned(orgId, pid),
          ticketDropdownApi.getCustomer(orgId, pid),
          ticketDropdownApi.getPartner(orgId, pid),
        ]);

        // TEAM
        team.forEach((item) => {
          if (!userMap.has(item.contactGUID)) {
            userMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Team)`,
            });
          }
        });

        // CUSTOMER
        customers.forEach((item) => {
          if (!userMap.has(item.contactGUID)) {
            userMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Customer)`,
            });
          }
        });

        // PARTNER
        partners.forEach((item) => {
          if (!userMap.has(item.contactGUID)) {
            userMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Partner)`,
            });
          }
        });
      }

      const sortedUsers = Array.from(userMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      setWorkLogUserOptions(sortedUsers);
    } catch (error) {
      console.error("Failed to build work log user options:", error);
      toast.error("Failed to load work log user options");
    } finally {
      setLoadingWorkLogUsers(false);
    }
  };

  // ========== FUNCTIONS FOR ASSIGNED TO (Team, Customer, Partner) ==========

  // Build assigned to options with specific project IDs
  const buildAssignedToOptionsWithProjects = async (projectIds) => {
    if (!projectIds || projectIds.length === 0) {
      setAssignedToOptions([]);
      return;
    }

    setLoadingAssignedTo(true);

    try {
      const assigneeMap = new Map();

      for (const pid of projectIds) {
        const [team, customers, partners] = await Promise.all([
          ticketDropdownApi.getTeamAssigned(orgId, pid),
          ticketDropdownApi.getCustomer(orgId, pid),
          ticketDropdownApi.getPartner(orgId, pid),
        ]);

        // TEAM
        team.forEach((item) => {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Team)`,
          });
        });

        // CUSTOMER
        customers.forEach((item) => {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Customer)`,
          });
        });

        // PARTNER
        partners.forEach((item) => {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Partner)`,
          });
        });
      }

      const finalList = Array.from(assigneeMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      setAssignedToOptions(finalList);
    } catch (error) {
      console.error("Failed to load assigned to options:", error);
      toast.error("Failed to load assignee options");
    } finally {
      setLoadingAssignedTo(false);
    }
  };

  // Build assigned to options based on current projectAssigned
  const buildAssignedToOptions = async () => {
    if (!updateForm.projectAssigned) {
      setAssignedToOptions([]);
      return;
    }

    const projectIds = updateForm.projectAssigned
      .split(",")
      .map((id) => id.trim());
    await buildAssignedToOptionsWithProjects(projectIds);
  };

  // ========== SUBMITTED BY (Separate API) ==========

  const loadSubmittedBy = async () => {
    if (!orgId) return;

    setLoadingSubmittedBy(true);
    try {
      const data = await ticketDropdownApi.getSubmittedBy(orgId);

      const uniqueMap = new Map();

      data.forEach((item) => {
        if (!uniqueMap.has(item.contactGuid)) {
          uniqueMap.set(item.contactGuid, {
            value: item.contactGuid,
            label: item.description,
          });
        }
      });

      const options = Array.from(uniqueMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      setSubmittedByOptions(options);
    } catch (error) {
      console.error("Failed to load Submitted By:", error);
    } finally {
      setLoadingSubmittedBy(false);
    }
  };

  // ========== PROJECTS BY SUBMITTED BY ==========

  const fetchProjectsBySubmittedBy = async (submittedByValue) => {
    if (!orgId || !submittedByValue) {
      setProjectOptionsBySubmitter([]);
      return;
    }

    setLoadingProjects(true);
    try {
      const projects = await ticketDropdownApi.getProjectsBySubmittedBy(
        orgId,
        submittedByValue,
      );

      // Format projects for Select component
      const formattedProjects = projects.map((project) => ({
        value: project.project_id,
        label: `${project.pr_Title} (${project.project_id})`,
      }));

      setProjectOptionsBySubmitter(formattedProjects);

      // If current projectAssigned is not in the new list, clear it
      if (updateForm.projectAssigned) {
        const currentProjectIds = updateForm.projectAssigned.split(",");
        const validProjects = currentProjectIds.filter((id) =>
          formattedProjects.some((p) => p.value === id),
        );

        if (validProjects.length !== currentProjectIds.length) {
          setUpdateForm((prev) => ({
            ...prev,
            projectAssigned: validProjects.join(","),
            assignedTo: [], // Clear assignedTo when project changes
          }));

          // Reload assignedTo options with new projects
          if (validProjects.length > 0) {
            await buildAssignedToOptionsWithProjects(validProjects);
            // Also reload work log user options
            await buildWorkLogUserOptions();
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch projects by submittedBy:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const getProjectCountText = () => {
    const selectedProjects =
      updateForm.projectAssigned?.split(",").filter((p) => p) || [];
    const totalProjects = projectOptionsBySubmitter.length;

    if (selectedProjects.length === 0) {
      return `0/${totalProjects} projects selected`;
    }
    return `${selectedProjects.length}/${totalProjects} projects selected`;
  };

  // ========== USER NAME RESOLUTION FOR DISPLAY USING getContactNames API ==========

  // Fetch user names in batch using getContactNames API
  const fetchUserNamesInBatch = async (userIds) => {
    if (!userIds || userIds.length === 0) return {};

    try {
      // Filter out IDs that are already in cache
      const uncachedIds = userIds.filter((id) => !userNamesCache[id]);

      if (uncachedIds.length === 0) {
        return userNamesCache;
      }

      // Call the bulk API to get contact names
      const contactGuids = uncachedIds.join(",");
      const contacts = await ticketDropdownApi.getContactNames(contactGuids);

      // Create a map of contactGuid to contactName
      const nameMap = {};
      contacts.forEach((contact) => {
        if (contact.contactGuid && contact.contactName) {
          nameMap[contact.contactGuid] = contact.contactName;
        }
      });

      // Update cache with fetched names
      setUserNamesCache((prev) => ({
        ...prev,
        ...nameMap,
      }));

      return nameMap;
    } catch (error) {
      console.error("Failed to fetch user names in batch:", error);
      return {};
    }
  };

  // Resolve work log user names using batch API
  const resolveWorkLogUserNames = async (workLogsByUser) => {
    if (!workLogsByUser || workLogsByUser.length === 0) return;

    const uniqueUserIds = [
      ...new Set(
        workLogsByUser.map((userWork) => userWork.userId).filter(Boolean),
      ),
    ];

    if (uniqueUserIds.length === 0) return;

    setLoadingUserNames(true);
    try {
      await fetchUserNamesInBatch(uniqueUserIds);
    } catch (error) {
      console.error("Error resolving user names:", error);
    } finally {
      setLoadingUserNames(false);
    }
  };

  // Load work logs data from API
  const loadWorkLogs = async () => {
    if (!ticket?.ticketId) return;

    setLoadingWorkLogs(true);
    try {
      const data = await getTicketWorkDetails(ticket.ticketId);
      setWorkLogsData(data);

      if (data?.workLogsByUser && data.workLogsByUser.length > 0) {
        await resolveWorkLogUserNames(data.workLogsByUser);
      }
    } catch (error) {
      console.error("Failed to load work logs:", error);
    } finally {
      setLoadingWorkLogs(false);
    }
  };

  // Load statuses from API
  const loadTicketStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const data = await getAllTicketStatuses();

      const mainStatuses = data.map((status) => ({
        value: status.statusGuid,
        label: status.statusName,
        statusId: status.statusId,
        requiresClosure: status.requiresClosure,
        isTerminal: status.isTerminal,
      }));

      setStatusOptions(mainStatuses);

      const subMap = {};
      data.forEach((mainStatus) => {
        if (mainStatus.subStatuses && mainStatus.subStatuses.length > 0) {
          subMap[mainStatus.statusGuid] = mainStatus.subStatuses.map((sub) => ({
            value: sub.statusGuid,
            label: sub.statusName,
            isDefault: sub.isDefault,
            requiresClosure: sub.requiresClosure,
            isTerminal: sub.isTerminal,
          }));
        } else {
          subMap[mainStatus.statusGuid] = [];
        }
      });

      setSubStatusMap(subMap);
    } catch (error) {
      console.error("Failed to load statuses:", error);
      toast.error("Failed to load status options");
    } finally {
      setLoadingStatuses(false);
    }
  };

  // ========== REFRESH FUNCTION ==========

  const refreshAllData = async () => {
    setRefreshing(true);
    try {
      // Clear cache
      setUserNamesCache({});

      // Reload work logs
      await loadWorkLogs();

      // Reload projects if submittedBy exists
      if (updateForm.submittedBy) {
        await fetchProjectsBySubmittedBy(updateForm.submittedBy);
      }

      // Reload assignedTo and workLogUser options based on current projects
      if (updateForm.projectAssigned) {
        const projectIds = updateForm.projectAssigned
          .split(",")
          .map((id) => id.trim());
        await buildAssignedToOptionsWithProjects(projectIds);
        await buildWorkLogUserOptions();
      }

      toast.success("Data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  // ========== TYPE, REASON, CATEGORY HANDLERS ==========

  const handleTypeChange = async (selected) => {
    const typeGuid = selected?.value || "";

    setUpdateForm({
      ...updateForm,
      ticketType: typeGuid,
      ticketReason: "",
      category: "",
    });

    setReasonOptions([]);
    setCategoryOptions([]);

    if (!typeGuid) return;

    try {
      const reasons = await ticketDropdownApi.getTicketLoggedType(
        orgId,
        typeGuid,
      );

      const formattedReasons = reasons.map((r) => ({
        value: r.ticket_log_guid,
        label: r.ticketdescription,
      }));

      setReasonOptions(formattedReasons);
    } catch (error) {
      console.error("Failed to load reasons:", error);
    }
  };

  const handleReasonChange = async (selected) => {
    const reasonGuid = selected?.value || "";

    setUpdateForm({
      ...updateForm,
      ticketReason: reasonGuid,
      category: "",
    });

    setCategoryOptions([]);

    if (!reasonGuid) return;

    try {
      const categories = await ticketDropdownApi.getTicketCategory(
        orgId,
        reasonGuid,
      );

      const formattedCategories = categories.map((c) => ({
        value: c.category_guid,
        label: c.ticketcategoryname,
      }));

      setCategoryOptions(formattedCategories);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const handleCategoryChange = (selected) => {
    setUpdateForm({
      ...updateForm,
      category: selected?.value || "",
    });
  };

  // ========== STATUS HANDLERS ==========

  const handleStatusChange = (selected) => {
    const statusGuid = selected?.value || "";
    const selectedStatus = statusOptions.find((s) => s.value === statusGuid);

    setUpdateForm({
      ...updateForm,
      status: statusGuid,
      subStatus: "",
    });

    const subStatuses = subStatusMap[statusGuid] || [];
    const defaultSubStatus = subStatuses.find((s) => s.isDefault);

    if (defaultSubStatus) {
      setUpdateForm((prev) => ({
        ...prev,
        subStatus: defaultSubStatus.value,
      }));
    }

    if (!selectedStatus?.isTerminal) {
      setClosureForm({
        closureTypeCode: "",
        closureRemarks: "",
        efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
      });
      setIsClosing(false);
    }
  };

  const handleSubStatusChange = (selected) => {
    setUpdateForm({
      ...updateForm,
      subStatus: selected?.value || "",
    });
  };

  // ========== SUBMITTED BY HANDLER ==========

  const handleSubmittedByChange = async (selected) => {
    const submittedByValue = selected?.value || "";

    setUpdateForm({
      ...updateForm,
      submittedBy: submittedByValue,
      projectAssigned: "", // Clear projects when submittedBy changes
      assignedTo: [], // Clear assignedTo
    });

    setAssignedToOptions([]); // Clear existing assignee options
    setWorkLogUserOptions([]); // Clear work log user options

    if (submittedByValue) {
      await fetchProjectsBySubmittedBy(submittedByValue);
    } else {
      setProjectOptionsBySubmitter([]);
    }
  };

  // ========== PROJECT HANDLER ==========

  const handleProjectChange = async (selected) => {
    const ids = selected ? selected.map((o) => o.value) : [];

    setUpdateForm({
      ...updateForm,
      projectAssigned: ids.join(","),
      assignedTo: [], // Clear assignedTo when project changes
    });

    setAssignedToOptions([]); // Clear assignee options while loading
    setWorkLogUserOptions([]); // Clear work log user options while loading

    if (ids.length > 0) {
      await buildAssignedToOptionsWithProjects(ids);
      await buildWorkLogUserOptions(); // Also reload work log options
    } else {
      setAssignedToOptions([]);
      setWorkLogUserOptions([]);
    }
  };

  // ========== WORK LOG MODAL HANDLER ==========

  const handleOpenWorkLogModal = async () => {
    // Refresh work log user options before opening modal
    if (updateForm.projectAssigned) {
      setLoadingWorkLogUsers(true);
      await buildWorkLogUserOptions();
      setLoadingWorkLogUsers(false);
    }
    setShowWorkLogModal(true);
  };

  // ========== CLOSURE HANDLERS ==========

  const handleCloseTicket = async () => {
    if (!closureForm.closureTypeCode) {
      toast.error("Please select closure type");
      return;
    }

    if (
      !closureForm.closureRemarks ||
      closureForm.closureRemarks.trim() === ""
    ) {
      toast.error("Please provide closure remarks");
      return;
    }

    for (const effort of closureForm.efforts) {
      if (!effort.workedBy) {
        toast.error("Please select worked by for all efforts");
        return;
      }
      if (!effort.effortValue || effort.effortValue <= 0) {
        toast.error("Please provide valid effort value for all efforts");
        return;
      }
    }

    setIsClosing(true);

    try {
      const payload = {
        ticketId: ticket.ticketId,
        orgId,
        closureTypeCode: closureForm.closureTypeCode,
        closureRemarks: closureForm.closureRemarks,
        createdBy: String(userDetails.userId),
        efforts: closureForm.efforts.map((e) => ({
          workedBy: e.workedBy,
          effortValue: Number(e.effortValue),
          effortUnit: e.effortUnit,
        })),
      };

      await closeTicket(payload);
      toast.success("Ticket closed successfully");

      setClosureForm({
        closureTypeCode: "",
        closureRemarks: "",
        efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
      });
      setIsClosing(false);

      refreshTickets();
      onClose();
    } catch (error) {
      console.error("Error closing ticket:", error);
      toast.error("Failed to close ticket");
      setIsClosing(false);
    }
  };

  // ========== WORK LOG HANDLER ==========

  const handleAddWorkLog = async () => {
    try {
      if (!workLogForm.workedBy || !workLogForm.effortValue) {
        toast.error("Fill all fields");
        return;
      }

      const payload = {
        ticketId: ticket.ticketId,
        orgId,
        createdBy: String(userDetails.userId),
        changedBy: workLogForm.workedBy,
        effortValue: Number(workLogForm.effortValue),
        effortUnit: workLogForm.effortUnit,
        closureRemarks: workLogForm.remarks,
      };

      await addTicketWorkLog(payload);
      toast.success("Work log added");
      setShowWorkLogModal(false);
      setWorkLogForm({
        workedBy: "",
        effortValue: "",
        effortUnit: "HOURS",
        remarks: "",
      });
      loadWorkLogs();
    } catch (error) {
      console.error("Failed to add work log:", error);
      toast.error("Failed to add work log");
    }
  };

  // ========== UPDATE TICKET HANDLER ==========

  const handleUpdateTicket = async () => {
    try {
      if (!updateForm.dueDate) {
        toast.error("Due Date is required");
        return;
      }

      setLoading(true);

      const payload = {
        ticketId: ticket.ticketId,
        ...updateForm,
        subStatus: updateForm.subStatus,
        assignedTo: updateForm.assignedTo.join(","),
        editedBy: String(userDetails.userId),
      };

      await updateTicket(ticket.ticketId, payload);
      toast.success("Ticket updated successfully");

      refreshTickets();
      onClose();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

  // ========== LOAD INITIAL DATA ==========

  useEffect(() => {
    if (orgId) {
      loadTicketStatuses();
      loadSubmittedBy();
      loadDropdownData();
    }
  }, [orgId]);

  // Load type options when modal opens
  useEffect(() => {
    const loadTypeOptionsForEdit = async () => {
      if (!show || !orgId) return;

      try {
        const types = await ticketDropdownApi.getTicketType(orgId);
        const formattedTypes = types.map((t) => ({
          value: t.type_GUID,
          label: t.ticketTypeName,
        }));
        setTypeOptions(formattedTypes);
      } catch (error) {
        console.error("Failed to load types for edit:", error);
      }
    };

    loadTypeOptionsForEdit();
  }, [show, orgId]);

  // Load cascading options (reasons and categories) based on ticket data
  useEffect(() => {
    const loadReasonsAndCategories = async () => {
      if (!show || !ticket) return;

      if (ticket.ticketType) {
        try {
          const reasons = await ticketDropdownApi.getTicketLoggedType(
            orgId,
            ticket.ticketType,
          );

          const formattedReasons = reasons.map((r) => ({
            value: r.ticket_log_guid,
            label: r.ticketdescription,
          }));

          setReasonOptions(formattedReasons);

          if (ticket.ticketReason) {
            const categories = await ticketDropdownApi.getTicketCategory(
              orgId,
              ticket.ticketReason,
            );

            const formattedCategories = categories.map((c) => ({
              value: c.category_guid,
              label: c.ticketcategoryname,
            }));

            setCategoryOptions(formattedCategories);
          }
        } catch (error) {
          console.error("Failed to load reasons/categories:", error);
        }
      }
    };

    loadReasonsAndCategories();
  }, [show, ticket?.ticketType, ticket?.ticketReason, orgId]);

  const loadDropdownData = async () => {
    try {
      if (!orgId) return;

      const types = await ticketDropdownApi.getTicketType(orgId);

      const formattedTypes = types.map((t) => ({
        value: t.type_GUID,
        label: t.ticketTypeName,
      }));

      setTypeOptions(formattedTypes);
    } catch (error) {
      toast.error("Failed to load ticket types");
    }
  };

  // Force re-render when user names are loaded
  useEffect(() => {
    if (
      Object.keys(userNamesCache).length > 0 &&
      workLogsData?.workLogsByUser
    ) {
      setWorkLogsData((prev) => ({ ...prev }));
    }
  }, [userNamesCache]);

  // Reset all states when modal opens with a new ticket
  useEffect(() => {
    if (show && ticket) {
      setWorkLogsData(null);
      setUserNamesCache({});
      setAssignedToOptions([]);
      setWorkLogUserOptions([]);
      setProjectOptionsBySubmitter([]);
      setClosureForm({
        closureTypeCode: "",
        closureRemarks: "",
        efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
      });
      setIsClosing(false);

      if (ticket.submittedBy) {
        fetchProjectsBySubmittedBy(ticket.submittedBy);
      }

      if (ticket.projectAssigned) {
        const projectIds = ticket.projectAssigned
          .split(",")
          .map((id) => id.trim());
        buildAssignedToOptionsWithProjects(projectIds);
        buildWorkLogUserOptions();
      }

      loadWorkLogs();
    }
  }, [show, ticket?.ticketId]);

  // Reset updateForm when ticket changes
  useEffect(() => {
    if (ticket) {
      setUpdateForm({
        ticketName: ticket.ticketName || "",
        description: ticket.description || "",
        status: ticket.status || "",
        subStatus: ticket.subStatus || "",
        priority: ticket.priority || "Medium",
        assignedTo: ticket.assignedTo
          ? ticket.assignedTo.split(",").map((i) => i.trim())
          : [],
        submittedBy: ticket.submittedBy || "",
        category: ticket.category || "",
        referenceTo: ticket.referenceTo || "",
        dueDate: ticket.dueDate
          ? moment(ticket.dueDate).format("YYYY-MM-DD")
          : "",
        projectAssigned: ticket.projectAssigned || "",
        ticketType: ticket.ticketType || "",
        ticketReason: ticket.ticketReason || "",
      });
    }
  }, [ticket]);

  // Refresh work log options when work log modal opens
  useEffect(() => {
    if (showWorkLogModal && updateForm.projectAssigned) {
      buildWorkLogUserOptions();
    }
  }, [showWorkLogModal, updateForm.projectAssigned]);

  useEffect(() => {
    loadTicketReasons();
    loadClosureTypes();
  }, []);

  const loadTicketReasons = async () => {
    try {
      const res = await getAllTicketLoggedTypes();
      setTicketReasons(res || []);
    } catch {
      toast.error("Failed to load ticket reasons");
    }
  };

  const loadClosureTypes = async () => {
    try {
      const res = await getAllTicketClosureTypes();
      setClosureTypes(res?.data || []);
    } catch {
      toast.error("Failed to load closure types");
    }
  };

  const closureTypeOptions = closureTypes
    .map((c) => ({
      value: c.codefix,
      label: c.description,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  if (!ticket) return null;

  const currentStatus = statusOptions.find(
    (s) => s.value === updateForm.status,
  );
  const currentSubStatuses = subStatusMap[updateForm.status] || [];

  const isStatusTerminal =
    currentStatus?.statusId === "CLOSED" ||
    currentStatus?.requiresClosure ||
    currentStatus?.isTerminal ||
    false;

  const getUserDisplayName = (userId) => {
    if (!userId) return "N/A";
    if (userNamesCache[userId]) {
      return userNamesCache[userId];
    }
    return userId;
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        dialogClassName="custom-modal-dialog-TEM"
        contentClassName="custom-modal-content-TEM"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Ticket {ticket.ticketId}</Modal.Title>
          <div
            className="d-flex gap-2"
            style={{ marginLeft: "auto", marginRight: "10px" }}
          >

            <Button
              variant="outline-info"
              size="sm"
              onClick={refreshAllData}
              disabled={refreshing}
              style={{ padding: refreshing ? "0.375rem 0.75rem" : "0.25rem" }}
            >
              {refreshing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Refreshing...
                </>
              ) : (
                <IoMdRefreshCircle style={{ fontSize: "3.25rem" }} />
              )}
            </Button>
            
            {/* Cancel Button */}
            <Button variant="secondary" onClick={onClose} >
              Cancel
            </Button>

            {/* Update/Close Button */}
            {isStatusTerminal ? (
              <Button
                variant="danger"
                onClick={handleCloseTicket}
                disabled={loading || isClosing}
              >
                {isClosing ? "Closing..." : "Close Ticket"}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleUpdateTicket}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update Ticket"}
              </Button>
            )}

            
          </div>
        </Modal.Header>

        <Modal.Body>
          <Row>
            {/* LEFT COLUMN - Edit Form */}
            <Col md={6} className="border-end">
              <div className="pe-3">
                <h6 className="mb-3 text-primary">Ticket Information</h6>

                {/* Loading Indicators */}
                {(loadingAssignedTo || loadingStatuses) && (
                  <div className="text-center py-3 mb-3 alert alert-info">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">
                      {loadingStatuses
                        ? "Loading status options..."
                        : "Loading assignee options..."}
                    </span>
                  </div>
                )}

                {/* Ticket Name */}
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={updateForm.ticketName}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        ticketName: e.target.value,
                      })
                    }
                  />
                </Form.Group>

                {/* Submitted By */}
                <Form.Group className="mb-3">
                  <Form.Label>Submitted By</Form.Label>
                  {loadingSubmittedBy ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Select
                      options={submittedByOptions}
                      value={
                        submittedByOptions.find(
                          (opt) => opt.value === updateForm.submittedBy,
                        ) || null
                      }
                      onChange={handleSubmittedByChange}
                      placeholder="Select submitter..."
                      isClearable
                    />
                  )}
                </Form.Group>

                {/* Ticket Type, Reason, Category */}
                <Row>
                  <Col md={12} className="mb-3">
                    <Form.Label>Ticket Type</Form.Label>
                    <Select
                      options={typeOptions}
                      value={typeOptions.find(
                        (opt) => opt.value === updateForm.ticketType,
                      )}
                      onChange={handleTypeChange}
                      placeholder="Select Type"
                    />
                  </Col>
                  <Col md={12} className="mb-3">
                    <Form.Label>Ticket Reason</Form.Label>
                    <Select
                      options={reasonOptions}
                      value={reasonOptions.find(
                        (opt) => opt.value === updateForm.ticketReason,
                      )}
                      onChange={handleReasonChange}
                      placeholder="Select Reason"
                      isDisabled={!updateForm.ticketType}
                    />
                  </Col>
                  <Col md={12} className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Select
                      options={categoryOptions}
                      value={categoryOptions.find(
                        (opt) => opt.value === updateForm.category,
                      )}
                      onChange={handleCategoryChange}
                      placeholder="Select Category"
                      isDisabled={!updateForm.ticketReason}
                    />
                  </Col>
                </Row>

                {/* Priority and Due Date */}
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={updateForm.priority}
                      onChange={(e) =>
                        setUpdateForm({
                          ...updateForm,
                          priority: e.target.value,
                        })
                      }
                    >
                      <option value="Low">Low</option>
                      <option value="Normal">Normal</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </Form.Select>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      required
                      value={updateForm.dueDate}
                      onChange={(e) =>
                        setUpdateForm({
                          ...updateForm,
                          dueDate: e.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>

                {/* Project Assigned */}
                <Form.Group className="mb-3">
                  <Form.Label>Project Assigned</Form.Label>
                  {loadingProjects ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Loading projects...</span>
                    </div>
                  ) : isProjectReadOnly ? (
                    <Form.Control
                      type="text"
                      value={
                        currentProject
                          ? `${currentProject.prTitle} (${currentProject.projectId})`
                          : updateForm.projectAssigned
                      }
                      disabled
                      className="bg-light"
                    />
                  ) : (
                    <>
                      <Select
                        isMulti
                        options={projectOptionsBySubmitter}
                        value={projectOptionsBySubmitter.filter((opt) =>
                          updateForm.projectAssigned
                            ?.split(",")
                            .includes(opt.value),
                        )}
                        onChange={handleProjectChange}
                        placeholder={
                          updateForm.submittedBy
                            ? "Select projects..."
                            : "Please select Submitted By first"
                        }
                        isDisabled={!updateForm.submittedBy || loadingProjects}
                        noOptionsMessage={() =>
                          !updateForm.submittedBy
                            ? "Please select Submitted By first"
                            : "No projects available for this submitter"
                        }
                      />
                      {updateForm.submittedBy &&
                        projectOptionsBySubmitter.length > 0 && (
                          <Form.Text className="text-muted">
                            <Badge
                              bg={
                                updateForm.projectAssigned
                                  ? "primary"
                                  : "secondary"
                              }
                              className="mt-1"
                              style={{ fontSize: "0.8rem" }}
                            >
                              {getProjectCountText()}
                            </Badge>
                          </Form.Text>
                        )}
                    </>
                  )}
                </Form.Group>

                {/* Assigned To */}
                <Form.Group className="mb-3">
                  <Form.Label>Assigned To</Form.Label>
                  {loadingAssignedTo ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Select
                      isMulti
                      options={assignedToOptions}
                      value={assignedToOptions.filter((opt) =>
                        updateForm.assignedTo.includes(opt.value),
                      )}
                      onChange={(selected) =>
                        setUpdateForm({
                          ...updateForm,
                          assignedTo: selected
                            ? selected.map((o) => o.value)
                            : [],
                        })
                      }
                      placeholder="Select assignees..."
                    />
                  )}
                  <small className="text-muted">
                    {assignedToOptions.length} assignee(s) available
                  </small>
                </Form.Group>

                {/* Status and Sub Status */}
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(
                        (opt) => opt.value === updateForm.status,
                      )}
                      onChange={handleStatusChange}
                      placeholder="Select Status"
                      isLoading={loadingStatuses}
                      isDisabled={loadingStatuses}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label>Sub Status</Form.Label>
                    <Select
                      options={currentSubStatuses}
                      value={currentSubStatuses.find(
                        (opt) => opt.value === updateForm.subStatus,
                      )}
                      onChange={handleSubStatusChange}
                      placeholder="Select Sub Status"
                      isDisabled={!updateForm.status || loadingStatuses}
                    />
                  </Col>
                </Row>

                {/* Description */}
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={updateForm.description}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        description: e.target.value,
                      })
                    }
                  />
                </Form.Group>

                {/* Inline Closure Form - Shown only for terminal statuses */}
                {isStatusTerminal && (
                  <div className="mt-4 p-3 border rounded bg-light">
                    <h6 className="mb-3 text-danger">Close Ticket Details</h6>

                    <Form.Group className="mb-3">
                      <Form.Label>Closure Type *</Form.Label>
                      <Select
                        options={closureTypeOptions}
                        value={closureTypeOptions.find(
                          (o) => o.value === closureForm.closureTypeCode,
                        )}
                        onChange={(opt) =>
                          setClosureForm({
                            ...closureForm,
                            closureTypeCode: opt?.value,
                          })
                        }
                        placeholder="Select closure type"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Closure Remarks *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={closureForm.closureRemarks}
                        onChange={(e) =>
                          setClosureForm({
                            ...closureForm,
                            closureRemarks: e.target.value,
                          })
                        }
                        placeholder="Provide closure remarks..."
                      />
                    </Form.Group>

                    <hr />
                    <h6 className="mb-3">Efforts (Work Log)</h6>
                    {closureForm.efforts.map((effort, index) => (
                      <Row key={index} className="mb-3">
                        <Col md={12} className="mb-2">
                          <Form.Label>Worked By *</Form.Label>
                          <Select
                            options={workLogUserOptions}
                            value={workLogUserOptions.find(
                              (o) => o.value === effort.workedBy,
                            )}
                            onChange={(opt) => {
                              const updated = [...closureForm.efforts];
                              updated[index].workedBy = opt?.value || "";
                              setClosureForm({
                                ...closureForm,
                                efforts: updated,
                              });
                            }}
                            placeholder="Select user"
                            isLoading={loadingWorkLogUsers}
                            isDisabled={!updateForm.projectAssigned}
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Effort *</Form.Label>
                          <Form.Control
                            type="number"
                            value={effort.effortValue}
                            onChange={(e) => {
                              const updated = [...closureForm.efforts];
                              updated[index].effortValue = e.target.value;
                              setClosureForm({
                                ...closureForm,
                                efforts: updated,
                              });
                            }}
                            placeholder="Hours"
                          />
                        </Col>
                        <Col md={4}>
                          <Form.Label>Unit</Form.Label>
                          <Form.Select
                            value={effort.effortUnit}
                            onChange={(e) => {
                              const updated = [...closureForm.efforts];
                              updated[index].effortUnit = e.target.value;
                              setClosureForm({
                                ...closureForm,
                                efforts: updated,
                              });
                            }}
                          >
                            <option value="HOURS">Hours</option>
                            <option value="DAYS">Days</option>
                          </Form.Select>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              const updated = closureForm.efforts.filter(
                                (_, i) => i !== index,
                              );
                              setClosureForm({
                                ...closureForm,
                                efforts: updated,
                              });
                            }}
                            disabled={closureForm.efforts.length === 1}
                          >
                            ✕
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() =>
                        setClosureForm({
                          ...closureForm,
                          efforts: [
                            ...closureForm.efforts,
                            {
                              workedBy: "",
                              effortValue: "",
                              effortUnit: "HOURS",
                            },
                          ],
                        })
                      }
                      disabled={!updateForm.projectAssigned}
                    >
                      + Add Effort
                    </Button>
                  </div>
                )}
              </div>
            </Col>

            {/* RIGHT COLUMN - Work Log History */}
            <Col md={6}>
              <div className="ps-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0 text-primary">Work Log History</h6>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={handleOpenWorkLogModal}
                  >
                    + Add Work Log
                  </Button>
                </div>

                {loadingWorkLogs || refreshing ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">
                      {refreshing
                        ? "Refreshing data..."
                        : "Loading work logs..."}
                    </span>
                  </div>
                ) : workLogsData?.workLogsByUser &&
                  workLogsData.workLogsByUser.length > 0 ? (
                  <>
                    <div className="mb-3 p-2 bg-light rounded">
                      <small className="text-muted">
                        Total Work Logged:{" "}
                        <strong>
                          {workLogsData.summary?.totalWorkLogHours || 0} hours
                        </strong>{" "}
                        | Total Entries:{" "}
                        <strong>
                          {workLogsData.summary?.totalWorkLogs || 0}
                        </strong>
                      </small>
                    </div>

                    <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                      {workLogsData.workLogsByUser.map((userWork) => (
                        <div
                          key={userWork.userId}
                          className="mb-3 border rounded p-2"
                        >
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">
                              <Badge bg="info" className="me-2">
                                {userWork.logCount} entry(s)
                              </Badge>
                              User:{" "}
                              <strong>
                                {loadingUserNames ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  getUserDisplayName(userWork.userId)
                                )}
                              </strong>
                            </h6>
                            <Badge bg="primary">
                              Total: {userWork.totalHours} hours
                            </Badge>
                          </div>

                          <Table size="sm" bordered hover className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Date</th>
                                <th>Hours</th>
                                <th>Unit</th>
                                <th>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userWork.logs.map((log, idx) => (
                                <tr key={log.ticketLogGuid || idx}>
                                  <td>
                                    {log.changedDate &&
                                    log.changedDate !== "0001-01-01T00:00:00"
                                      ? moment(log.changedDate).format(
                                          "DD-MMM-YYYY",
                                        )
                                      : moment(log.createdDate).format(
                                          "DD-MMM-YYYY",
                                        )}
                                  </td>
                                  <td>{log.effortValue}</td>
                                  <td>{log.effortUnit || "HOURS"}</td>
                                  <td
                                    className="text-wrap"
                                    style={{ maxWidth: "250px" }}
                                  >
                                    {log.closureRemarks || "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5 text-muted border rounded">
                    <i>No work logs found for this ticket</i>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>


      </Modal>

      {/* Work Log Modal */}
      <Modal
        show={showWorkLogModal}
        onHide={() => setShowWorkLogModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Work Log</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Worked By</Form.Label>
              {loadingWorkLogUsers ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading users...</span>
                </div>
              ) : (
                <Select
                  options={workLogUserOptions}
                  value={workLogUserOptions.find(
                    (o) => o.value === workLogForm.workedBy,
                  )}
                  onChange={(opt) =>
                    setWorkLogForm({
                      ...workLogForm,
                      workedBy: opt?.value,
                    })
                  }
                  placeholder={
                    updateForm.projectAssigned
                      ? "Select user..."
                      : "Please select a project first"
                  }
                  isDisabled={
                    !updateForm.projectAssigned || loadingWorkLogUsers
                  }
                  noOptionsMessage={() =>
                    !updateForm.projectAssigned
                      ? "Please select a project first"
                      : "No users available for selected project(s)"
                  }
                />
              )}
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Effort (Hours)</Form.Label>
                  <Form.Control
                    type="number"
                    value={workLogForm.effortValue}
                    onChange={(e) =>
                      setWorkLogForm({
                        ...workLogForm,
                        effortValue: e.target.value,
                      })
                    }
                    placeholder="Enter effort hours"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Select
                    value={workLogForm.effortUnit}
                    onChange={(e) =>
                      setWorkLogForm({
                        ...workLogForm,
                        effortUnit: e.target.value,
                      })
                    }
                  >
                    <option value="HOURS">Hours</option>
                    <option value="DAYS">Days</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={workLogForm.remarks}
                onChange={(e) =>
                  setWorkLogForm({
                    ...workLogForm,
                    remarks: e.target.value,
                  })
                }
                placeholder="Enter work log remarks..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowWorkLogModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddWorkLog}
            disabled={
              !updateForm.projectAssigned ||
              !workLogForm.workedBy ||
              !workLogForm.effortValue
            }
          >
            Save Work Log
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TicketEditModal;

// import { useState, useEffect, useCallback } from "react";
// import {
//   Modal,
//   Button,
//   Form,
//   Row,
//   Col,
//   Spinner,
//   Table,
//   Badge,
// } from "react-bootstrap";
// import Select from "react-select";
// import moment from "moment";
// import { toast } from "react-toastify";

// import {
//   updateTicket,
//   closeTicket,
//   addTicketWorkLog,
//   getAllTicketClosureTypes,
//   getAllTicketLoggedTypes,
//   getAllTicketStatuses,
//   getTicketWorkDetails,
// } from "../Api/TicketApiOrg";
// import { ticketDropdownApi } from "../Api/TicketApiOrg";
// import { fetchResourceById } from "../Api/ResourceMaster";
// import { getResourceByMavUserId } from "../Api/ResourceList";
// import { fetchCusVenCpById } from "../Api/CusVenCpMasterApi";

// const TicketEditModal = ({
//   show,
//   onClose,
//   ticket,
//   orgId,
//   userDetails,
//   projectOptions,
//   fetchProjectAndPartners,
//   refreshTickets,
//   currentProject,
//   isProjectReadOnly = false,
// }) => {
//   const [loading, setLoading] = useState(false);
//   const [showWorkLogModal, setShowWorkLogModal] = useState(false);
//   const [ticketReasons, setTicketReasons] = useState([]);
//   const [closureTypes, setClosureTypes] = useState([]);
//   const [refreshing, setRefreshing] = useState(false);

//   // State for work logs
//   const [workLogsData, setWorkLogsData] = useState(null);
//   const [loadingWorkLogs, setLoadingWorkLogs] = useState(false);

//   // State for user names cache
//   const [userNamesCache, setUserNamesCache] = useState({});
//   const [loadingUserNames, setLoadingUserNames] = useState(false);

//   const [submittedByOptions, setSubmittedByOptions] = useState([]);
//   const [loadingSubmittedBy, setLoadingSubmittedBy] = useState(false);

//   // Projects by Submitter
//   const [projectOptionsBySubmitter, setProjectOptionsBySubmitter] = useState(
//     [],
//   );
//   const [loadingProjects, setLoadingProjects] = useState(false);

//   // Status Master Data
//   const [statusOptions, setStatusOptions] = useState([]);
//   const [subStatusMap, setSubStatusMap] = useState({});
//   const [loadingStatuses, setLoadingStatuses] = useState(false);

//   // ========== SEPARATE LISTS FOR DIFFERENT PURPOSES ==========

//   // 1. For "Assigned To" field - includes Team, Customer, Partner (with labels)
//   const [assignedToOptions, setAssignedToOptions] = useState([]);
//   const [loadingAssignedTo, setLoadingAssignedTo] = useState(false);

//   // 2. For "Submitted By" field - uses separate API
//   // Already using submittedByOptions state above

//   // 3. For "Work Log Worked By" field - NOW SAME AS ASSIGNED TO (Team, Customer, Partner)
//   const [workLogUserOptions, setWorkLogUserOptions] = useState([]);
//   const [loadingWorkLogUsers, setLoadingWorkLogUsers] = useState(false);

//   const [typeOptions, setTypeOptions] = useState([]);
//   const [reasonOptions, setReasonOptions] = useState([]);
//   const [categoryOptions, setCategoryOptions] = useState([]);

//   const [updateForm, setUpdateForm] = useState({
//     ticketName: "",
//     description: "",
//     status: "",
//     priority: "",
//     assignedTo: [],
//     submittedBy: "",
//     category: "",
//     dueDate: "",
//     referenceTo: "",
//     projectAssigned: "",
//     ticketType: "",
//     ticketReason: "",
//     subStatus: "",
//   });

//   // Closure form state (inline)
//   const [closureForm, setClosureForm] = useState({
//     closureTypeCode: "",
//     closureRemarks: "",
//     efforts: [
//       {
//         workedBy: "",
//         effortValue: "",
//         effortUnit: "HOURS",
//       },
//     ],
//   });
//   const [isClosing, setIsClosing] = useState(false);

//   const [workLogForm, setWorkLogForm] = useState({
//     workedBy: "",
//     effortValue: "",
//     effortUnit: "HOURS",
//     remarks: "",
//   });

//   // Helper to check if a string is a name (contains spaces and letters)
//   const isNameString = (str) => {
//     if (!str) return false;
//     return (
//       /^[A-Za-z\s]+$/.test(str) && !str.startsWith("CVCP") && isNaN(Number(str))
//     );
//   };

//   // ========== FUNCTIONS FOR WORK LOG USERS (SAME AS ASSIGNED TO) ==========

//   // Build work log user options (SAME AS ASSIGNED TO - Team, Customer, Partner)
//   const buildWorkLogUserOptions = async () => {
//     if (!updateForm.projectAssigned || !orgId) {
//       setWorkLogUserOptions([]);
//       return;
//     }

//     setLoadingWorkLogUsers(true);

//     try {
//       const projectIds = updateForm.projectAssigned
//         .split(",")
//         .map((id) => id.trim());

//       const userMap = new Map();

//       for (const pid of projectIds) {
//         const [team, customers, partners] = await Promise.all([
//           ticketDropdownApi.getTeamAssigned(orgId, pid),
//           ticketDropdownApi.getCustomer(orgId, pid),
//           ticketDropdownApi.getPartner(orgId, pid),
//         ]);

//         // TEAM
//         team.forEach((item) => {
//           if (!userMap.has(item.contactGUID)) {
//             userMap.set(item.contactGUID, {
//               value: item.contactGUID,
//               label: `${item.contactName} (Team)`,
//             });
//           }
//         });

//         // CUSTOMER
//         customers.forEach((item) => {
//           if (!userMap.has(item.contactGUID)) {
//             userMap.set(item.contactGUID, {
//               value: item.contactGUID,
//               label: `${item.contactName} (Customer)`,
//             });
//           }
//         });

//         // PARTNER
//         partners.forEach((item) => {
//           if (!userMap.has(item.contactGUID)) {
//             userMap.set(item.contactGUID, {
//               value: item.contactGUID,
//               label: `${item.contactName} (Partner)`,
//             });
//           }
//         });
//       }

//       const sortedUsers = Array.from(userMap.values()).sort((a, b) =>
//         a.label.localeCompare(b.label),
//       );

//       setWorkLogUserOptions(sortedUsers);
//     } catch (error) {
//       console.error("Failed to build work log user options:", error);
//       toast.error("Failed to load work log user options");
//     } finally {
//       setLoadingWorkLogUsers(false);
//     }
//   };

//   // ========== FUNCTIONS FOR ASSIGNED TO (Team, Customer, Partner) ==========

//   // Build assigned to options with specific project IDs
//   const buildAssignedToOptionsWithProjects = async (projectIds) => {
//     if (!projectIds || projectIds.length === 0) {
//       setAssignedToOptions([]);
//       return;
//     }

//     setLoadingAssignedTo(true);

//     try {
//       const assigneeMap = new Map();

//       for (const pid of projectIds) {
//         const [team, customers, partners] = await Promise.all([
//           ticketDropdownApi.getTeamAssigned(orgId, pid),
//           ticketDropdownApi.getCustomer(orgId, pid),
//           ticketDropdownApi.getPartner(orgId, pid),
//         ]);

//         // TEAM
//         team.forEach((item) => {
//           assigneeMap.set(item.contactGUID, {
//             value: item.contactGUID,
//             label: `${item.contactName} (Team)`,
//           });
//         });

//         // CUSTOMER
//         customers.forEach((item) => {
//           assigneeMap.set(item.contactGUID, {
//             value: item.contactGUID,
//             label: `${item.contactName} (Customer)`,
//           });
//         });

//         // PARTNER
//         partners.forEach((item) => {
//           assigneeMap.set(item.contactGUID, {
//             value: item.contactGUID,
//             label: `${item.contactName} (Partner)`,
//           });
//         });
//       }

//       const finalList = Array.from(assigneeMap.values()).sort((a, b) =>
//         a.label.localeCompare(b.label),
//       );

//       setAssignedToOptions(finalList);
//     } catch (error) {
//       console.error("Failed to load assigned to options:", error);
//       toast.error("Failed to load assignee options");
//     } finally {
//       setLoadingAssignedTo(false);
//     }
//   };

//   // Build assigned to options based on current projectAssigned
//   const buildAssignedToOptions = async () => {
//     if (!updateForm.projectAssigned) {
//       setAssignedToOptions([]);
//       return;
//     }

//     const projectIds = updateForm.projectAssigned
//       .split(",")
//       .map((id) => id.trim());
//     await buildAssignedToOptionsWithProjects(projectIds);
//   };

//   // ========== SUBMITTED BY (Separate API) ==========

//   const loadSubmittedBy = async () => {
//     if (!orgId) return;

//     setLoadingSubmittedBy(true);
//     try {
//       const data = await ticketDropdownApi.getSubmittedBy(orgId);

//       const uniqueMap = new Map();

//       data.forEach((item) => {
//         if (!uniqueMap.has(item.contactGuid)) {
//           uniqueMap.set(item.contactGuid, {
//             value: item.contactGuid,
//             label: item.description,
//           });
//         }
//       });

//       const options = Array.from(uniqueMap.values()).sort((a, b) =>
//         a.label.localeCompare(b.label),
//       );

//       setSubmittedByOptions(options);
//     } catch (error) {
//       console.error("Failed to load Submitted By:", error);
//     } finally {
//       setLoadingSubmittedBy(false);
//     }
//   };

//   // ========== PROJECTS BY SUBMITTED BY ==========

//   const fetchProjectsBySubmittedBy = async (submittedByValue) => {
//     if (!orgId || !submittedByValue) {
//       setProjectOptionsBySubmitter([]);
//       return;
//     }

//     setLoadingProjects(true);
//     try {
//       const projects = await ticketDropdownApi.getProjectsBySubmittedBy(
//         orgId,
//         submittedByValue,
//       );

//       // Format projects for Select component
//       const formattedProjects = projects.map((project) => ({
//         value: project.project_id,
//         label: `${project.pr_Title} (${project.project_id})`,
//       }));

//       setProjectOptionsBySubmitter(formattedProjects);

//       // If current projectAssigned is not in the new list, clear it
//       if (updateForm.projectAssigned) {
//         const currentProjectIds = updateForm.projectAssigned.split(",");
//         const validProjects = currentProjectIds.filter((id) =>
//           formattedProjects.some((p) => p.value === id),
//         );

//         if (validProjects.length !== currentProjectIds.length) {
//           setUpdateForm((prev) => ({
//             ...prev,
//             projectAssigned: validProjects.join(","),
//             assignedTo: [], // Clear assignedTo when project changes
//           }));

//           // Reload assignedTo options with new projects
//           if (validProjects.length > 0) {
//             await buildAssignedToOptionsWithProjects(validProjects);
//             // Also reload work log user options
//             await buildWorkLogUserOptions();
//           }
//         }
//       }
//     } catch (error) {
//       console.error("Failed to fetch projects by submittedBy:", error);
//       toast.error("Failed to load projects");
//     } finally {
//       setLoadingProjects(false);
//     }
//   };

//   const getProjectCountText = () => {
//     const selectedProjects =
//       updateForm.projectAssigned?.split(",").filter((p) => p) || [];
//     const totalProjects = projectOptionsBySubmitter.length;

//     if (selectedProjects.length === 0) {
//       return `0/${totalProjects} projects selected`;
//     }
//     return `${selectedProjects.length}/${totalProjects} projects selected`;
//   };

//   // ========== USER NAME RESOLUTION FOR DISPLAY USING getContactNames API ==========

//   // Fetch user names in batch using getContactNames API
//   const fetchUserNamesInBatch = async (userIds) => {
//     if (!userIds || userIds.length === 0) return {};

//     try {
//       // Filter out IDs that are already in cache
//       const uncachedIds = userIds.filter((id) => !userNamesCache[id]);

//       if (uncachedIds.length === 0) {
//         return userNamesCache;
//       }

//       // Call the bulk API to get contact names
//       const contactGuids = uncachedIds.join(",");
//       const contacts = await ticketDropdownApi.getContactNames(contactGuids);

//       // Create a map of contactGuid to contactName
//       const nameMap = {};
//       contacts.forEach((contact) => {
//         if (contact.contactGuid && contact.contactName) {
//           nameMap[contact.contactGuid] = contact.contactName;
//         }
//       });

//       // Update cache with fetched names
//       setUserNamesCache((prev) => ({
//         ...prev,
//         ...nameMap,
//       }));

//       return nameMap;
//     } catch (error) {
//       console.error("Failed to fetch user names in batch:", error);
//       return {};
//     }
//   };

//   // Resolve work log user names using batch API
//   const resolveWorkLogUserNames = async (workLogsByUser) => {
//     if (!workLogsByUser || workLogsByUser.length === 0) return;

//     const uniqueUserIds = [
//       ...new Set(
//         workLogsByUser.map((userWork) => userWork.userId).filter(Boolean),
//       ),
//     ];

//     if (uniqueUserIds.length === 0) return;

//     setLoadingUserNames(true);
//     try {
//       await fetchUserNamesInBatch(uniqueUserIds);
//     } catch (error) {
//       console.error("Error resolving user names:", error);
//     } finally {
//       setLoadingUserNames(false);
//     }
//   };

//   // Load work logs data from API
//   const loadWorkLogs = async () => {
//     if (!ticket?.ticketId) return;

//     setLoadingWorkLogs(true);
//     try {
//       const data = await getTicketWorkDetails(ticket.ticketId);
//       setWorkLogsData(data);

//       if (data?.workLogsByUser && data.workLogsByUser.length > 0) {
//         await resolveWorkLogUserNames(data.workLogsByUser);
//       }
//     } catch (error) {
//       console.error("Failed to load work logs:", error);
//     } finally {
//       setLoadingWorkLogs(false);
//     }
//   };

//   // Load statuses from API
//   const loadTicketStatuses = async () => {
//     setLoadingStatuses(true);
//     try {
//       const data = await getAllTicketStatuses();

//       const mainStatuses = data.map((status) => ({
//         value: status.statusGuid,
//         label: status.statusName,
//         statusId: status.statusId,
//         requiresClosure: status.requiresClosure,
//         isTerminal: status.isTerminal,
//       }));

//       setStatusOptions(mainStatuses);

//       const subMap = {};
//       data.forEach((mainStatus) => {
//         if (mainStatus.subStatuses && mainStatus.subStatuses.length > 0) {
//           subMap[mainStatus.statusGuid] = mainStatus.subStatuses.map((sub) => ({
//             value: sub.statusGuid,
//             label: sub.statusName,
//             isDefault: sub.isDefault,
//             requiresClosure: sub.requiresClosure,
//             isTerminal: sub.isTerminal,
//           }));
//         } else {
//           subMap[mainStatus.statusGuid] = [];
//         }
//       });

//       setSubStatusMap(subMap);
//     } catch (error) {
//       console.error("Failed to load statuses:", error);
//       toast.error("Failed to load status options");
//     } finally {
//       setLoadingStatuses(false);
//     }
//   };

//   // ========== REFRESH FUNCTION ==========

//   const refreshAllData = async () => {
//     setRefreshing(true);
//     try {
//       // Clear cache
//       setUserNamesCache({});

//       // Reload work logs
//       await loadWorkLogs();

//       // Reload projects if submittedBy exists
//       if (updateForm.submittedBy) {
//         await fetchProjectsBySubmittedBy(updateForm.submittedBy);
//       }

//       // Reload assignedTo and workLogUser options based on current projects
//       if (updateForm.projectAssigned) {
//         const projectIds = updateForm.projectAssigned
//           .split(",")
//           .map((id) => id.trim());
//         await buildAssignedToOptionsWithProjects(projectIds);
//         await buildWorkLogUserOptions();
//       }

//       toast.success("Data refreshed successfully");
//     } catch (error) {
//       console.error("Error refreshing data:", error);
//       toast.error("Failed to refresh data");
//     } finally {
//       setRefreshing(false);
//     }
//   };

//   // ========== TYPE, REASON, CATEGORY HANDLERS ==========

//   const handleTypeChange = async (selected) => {
//     const typeGuid = selected?.value || "";

//     setUpdateForm({
//       ...updateForm,
//       ticketType: typeGuid,
//       ticketReason: "",
//       category: "",
//     });

//     setReasonOptions([]);
//     setCategoryOptions([]);

//     if (!typeGuid) return;

//     try {
//       const reasons = await ticketDropdownApi.getTicketLoggedType(
//         orgId,
//         typeGuid,
//       );

//       const formattedReasons = reasons.map((r) => ({
//         value: r.ticket_log_guid,
//         label: r.ticketdescription,
//       }));

//       setReasonOptions(formattedReasons);
//     } catch (error) {
//       console.error("Failed to load reasons:", error);
//     }
//   };

//   const handleReasonChange = async (selected) => {
//     const reasonGuid = selected?.value || "";

//     setUpdateForm({
//       ...updateForm,
//       ticketReason: reasonGuid,
//       category: "",
//     });

//     setCategoryOptions([]);

//     if (!reasonGuid) return;

//     try {
//       const categories = await ticketDropdownApi.getTicketCategory(
//         orgId,
//         reasonGuid,
//       );

//       const formattedCategories = categories.map((c) => ({
//         value: c.category_guid,
//         label: c.ticketcategoryname,
//       }));

//       setCategoryOptions(formattedCategories);
//     } catch (error) {
//       console.error("Failed to load categories:", error);
//     }
//   };

//   const handleCategoryChange = (selected) => {
//     setUpdateForm({
//       ...updateForm,
//       category: selected?.value || "",
//     });
//   };

//   // ========== STATUS HANDLERS ==========

//   const handleStatusChange = (selected) => {
//     const statusGuid = selected?.value || "";
//     const selectedStatus = statusOptions.find((s) => s.value === statusGuid);

//     setUpdateForm({
//       ...updateForm,
//       status: statusGuid,
//       subStatus: "",
//     });

//     const subStatuses = subStatusMap[statusGuid] || [];
//     const defaultSubStatus = subStatuses.find((s) => s.isDefault);

//     if (defaultSubStatus) {
//       setUpdateForm((prev) => ({
//         ...prev,
//         subStatus: defaultSubStatus.value,
//       }));
//     }

//     if (!selectedStatus?.isTerminal) {
//       setClosureForm({
//         closureTypeCode: "",
//         closureRemarks: "",
//         efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
//       });
//       setIsClosing(false);
//     }
//   };

//   const handleSubStatusChange = (selected) => {
//     setUpdateForm({
//       ...updateForm,
//       subStatus: selected?.value || "",
//     });
//   };

//   // ========== SUBMITTED BY HANDLER ==========

//   const handleSubmittedByChange = async (selected) => {
//     const submittedByValue = selected?.value || "";

//     setUpdateForm({
//       ...updateForm,
//       submittedBy: submittedByValue,
//       projectAssigned: "", // Clear projects when submittedBy changes
//       assignedTo: [], // Clear assignedTo
//     });

//     setAssignedToOptions([]); // Clear existing assignee options
//     setWorkLogUserOptions([]); // Clear work log user options

//     if (submittedByValue) {
//       await fetchProjectsBySubmittedBy(submittedByValue);
//     } else {
//       setProjectOptionsBySubmitter([]);
//     }
//   };

//   // ========== PROJECT HANDLER ==========

//   const handleProjectChange = async (selected) => {
//     const ids = selected ? selected.map((o) => o.value) : [];

//     setUpdateForm({
//       ...updateForm,
//       projectAssigned: ids.join(","),
//       assignedTo: [], // Clear assignedTo when project changes
//     });

//     setAssignedToOptions([]); // Clear assignee options while loading
//     setWorkLogUserOptions([]); // Clear work log user options while loading

//     if (ids.length > 0) {
//       await buildAssignedToOptionsWithProjects(ids);
//       await buildWorkLogUserOptions(); // Also reload work log options
//     } else {
//       setAssignedToOptions([]);
//       setWorkLogUserOptions([]);
//     }
//   };

//   // ========== WORK LOG MODAL HANDLER ==========

//   const handleOpenWorkLogModal = async () => {
//     // Refresh work log user options before opening modal
//     if (updateForm.projectAssigned) {
//       setLoadingWorkLogUsers(true);
//       await buildWorkLogUserOptions();
//       setLoadingWorkLogUsers(false);
//     }
//     setShowWorkLogModal(true);
//   };

//   // ========== CLOSURE HANDLERS ==========

//   const handleCloseTicket = async () => {
//     if (!closureForm.closureTypeCode) {
//       toast.error("Please select closure type");
//       return;
//     }

//     if (
//       !closureForm.closureRemarks ||
//       closureForm.closureRemarks.trim() === ""
//     ) {
//       toast.error("Please provide closure remarks");
//       return;
//     }

//     for (const effort of closureForm.efforts) {
//       if (!effort.workedBy) {
//         toast.error("Please select worked by for all efforts");
//         return;
//       }
//       if (!effort.effortValue || effort.effortValue <= 0) {
//         toast.error("Please provide valid effort value for all efforts");
//         return;
//       }
//     }

//     setIsClosing(true);

//     try {
//       const payload = {
//         ticketId: ticket.ticketId,
//         orgId,
//         closureTypeCode: closureForm.closureTypeCode,
//         closureRemarks: closureForm.closureRemarks,
//         createdBy: String(userDetails.userId),
//         efforts: closureForm.efforts.map((e) => ({
//           workedBy: e.workedBy,
//           effortValue: Number(e.effortValue),
//           effortUnit: e.effortUnit,
//         })),
//       };

//       await closeTicket(payload);
//       toast.success("Ticket closed successfully");

//       setClosureForm({
//         closureTypeCode: "",
//         closureRemarks: "",
//         efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
//       });
//       setIsClosing(false);

//       refreshTickets();
//       onClose();
//     } catch (error) {
//       console.error("Error closing ticket:", error);
//       toast.error("Failed to close ticket");
//       setIsClosing(false);
//     }
//   };

//   // ========== WORK LOG HANDLER ==========

//   const handleAddWorkLog = async () => {
//     try {
//       if (!workLogForm.workedBy || !workLogForm.effortValue) {
//         toast.error("Fill all fields");
//         return;
//       }

//       const payload = {
//         ticketId: ticket.ticketId,
//         orgId,
//         createdBy: String(userDetails.userId),
//         changedBy: workLogForm.workedBy,
//         effortValue: Number(workLogForm.effortValue),
//         effortUnit: workLogForm.effortUnit,
//         closureRemarks: workLogForm.remarks,
//       };

//       await addTicketWorkLog(payload);
//       toast.success("Work log added");
//       setShowWorkLogModal(false);
//       setWorkLogForm({
//         workedBy: "",
//         effortValue: "",
//         effortUnit: "HOURS",
//         remarks: "",
//       });
//       loadWorkLogs();
//     } catch (error) {
//       console.error("Failed to add work log:", error);
//       toast.error("Failed to add work log");
//     }
//   };

//   // ========== UPDATE TICKET HANDLER ==========

//   const handleUpdateTicket = async () => {
//     try {
//       if (!updateForm.dueDate) {
//         toast.error("Due Date is required");
//         return;
//       }

//       setLoading(true);

//       const payload = {
//         ticketId: ticket.ticketId,
//         ...updateForm,
//         subStatus: updateForm.subStatus,
//         assignedTo: updateForm.assignedTo.join(","),
//         editedBy: String(userDetails.userId),
//       };

//       await updateTicket(ticket.ticketId, payload);
//       toast.success("Ticket updated successfully");

//       refreshTickets();
//       onClose();
//     } catch (error) {
//       console.error("Update error:", error);
//       toast.error("Failed to update ticket");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ========== LOAD INITIAL DATA ==========

//   useEffect(() => {
//     if (orgId) {
//       loadTicketStatuses();
//       loadSubmittedBy();
//       loadDropdownData();
//     }
//   }, [orgId]);

//   // Load type options when modal opens
//   useEffect(() => {
//     const loadTypeOptionsForEdit = async () => {
//       if (!show || !orgId) return;

//       try {
//         const types = await ticketDropdownApi.getTicketType(orgId);
//         const formattedTypes = types.map((t) => ({
//           value: t.type_GUID,
//           label: t.ticketTypeName,
//         }));
//         setTypeOptions(formattedTypes);
//       } catch (error) {
//         console.error("Failed to load types for edit:", error);
//       }
//     };

//     loadTypeOptionsForEdit();
//   }, [show, orgId]);

//   // Load cascading options (reasons and categories) based on ticket data
//   useEffect(() => {
//     const loadReasonsAndCategories = async () => {
//       if (!show || !ticket) return;

//       if (ticket.ticketType) {
//         try {
//           const reasons = await ticketDropdownApi.getTicketLoggedType(
//             orgId,
//             ticket.ticketType,
//           );

//           const formattedReasons = reasons.map((r) => ({
//             value: r.ticket_log_guid,
//             label: r.ticketdescription,
//           }));

//           setReasonOptions(formattedReasons);

//           if (ticket.ticketReason) {
//             const categories = await ticketDropdownApi.getTicketCategory(
//               orgId,
//               ticket.ticketReason,
//             );

//             const formattedCategories = categories.map((c) => ({
//               value: c.category_guid,
//               label: c.ticketcategoryname,
//             }));

//             setCategoryOptions(formattedCategories);
//           }
//         } catch (error) {
//           console.error("Failed to load reasons/categories:", error);
//         }
//       }
//     };

//     loadReasonsAndCategories();
//   }, [show, ticket?.ticketType, ticket?.ticketReason, orgId]);

//   const loadDropdownData = async () => {
//     try {
//       if (!orgId) return;

//       const types = await ticketDropdownApi.getTicketType(orgId);

//       const formattedTypes = types.map((t) => ({
//         value: t.type_GUID,
//         label: t.ticketTypeName,
//       }));

//       setTypeOptions(formattedTypes);
//     } catch (error) {
//       toast.error("Failed to load ticket types");
//     }
//   };

//   // Force re-render when user names are loaded
//   useEffect(() => {
//     if (
//       Object.keys(userNamesCache).length > 0 &&
//       workLogsData?.workLogsByUser
//     ) {
//       setWorkLogsData((prev) => ({ ...prev }));
//     }
//   }, [userNamesCache]);

//   // Reset all states when modal opens with a new ticket
//   useEffect(() => {
//     if (show && ticket) {
//       setWorkLogsData(null);
//       setUserNamesCache({});
//       setAssignedToOptions([]);
//       setWorkLogUserOptions([]);
//       setProjectOptionsBySubmitter([]);
//       setClosureForm({
//         closureTypeCode: "",
//         closureRemarks: "",
//         efforts: [{ workedBy: "", effortValue: "", effortUnit: "HOURS" }],
//       });
//       setIsClosing(false);

//       if (ticket.submittedBy) {
//         fetchProjectsBySubmittedBy(ticket.submittedBy);
//       }

//       if (ticket.projectAssigned) {
//         const projectIds = ticket.projectAssigned
//           .split(",")
//           .map((id) => id.trim());
//         buildAssignedToOptionsWithProjects(projectIds);
//         buildWorkLogUserOptions();
//       }

//       loadWorkLogs();
//     }
//   }, [show, ticket?.ticketId]);

//   // Reset updateForm when ticket changes
//   useEffect(() => {
//     if (ticket) {
//       setUpdateForm({
//         ticketName: ticket.ticketName || "",
//         description: ticket.description || "",
//         status: ticket.status || "",
//         subStatus: ticket.subStatus || "",
//         priority: ticket.priority || "Medium",
//         assignedTo: ticket.assignedTo
//           ? ticket.assignedTo.split(",").map((i) => i.trim())
//           : [],
//         submittedBy: ticket.submittedBy || "",
//         category: ticket.category || "",
//         referenceTo: ticket.referenceTo || "",
//         dueDate: ticket.dueDate
//           ? moment(ticket.dueDate).format("YYYY-MM-DD")
//           : "",
//         projectAssigned: ticket.projectAssigned || "",
//         ticketType: ticket.ticketType || "",
//         ticketReason: ticket.ticketReason || "",
//       });
//     }
//   }, [ticket]);

//   // Refresh work log options when work log modal opens
//   useEffect(() => {
//     if (showWorkLogModal && updateForm.projectAssigned) {
//       buildWorkLogUserOptions();
//     }
//   }, [showWorkLogModal, updateForm.projectAssigned]);

//   useEffect(() => {
//     loadTicketReasons();
//     loadClosureTypes();
//   }, []);

//   const loadTicketReasons = async () => {
//     try {
//       const res = await getAllTicketLoggedTypes();
//       setTicketReasons(res || []);
//     } catch {
//       toast.error("Failed to load ticket reasons");
//     }
//   };

//   const loadClosureTypes = async () => {
//     try {
//       const res = await getAllTicketClosureTypes();
//       setClosureTypes(res?.data || []);
//     } catch {
//       toast.error("Failed to load closure types");
//     }
//   };

//   const closureTypeOptions = closureTypes
//     .map((c) => ({
//       value: c.codefix,
//       label: c.description,
//     }))
//     .sort((a, b) => a.label.localeCompare(b.label));

//   if (!ticket) return null;

//   const currentStatus = statusOptions.find(
//     (s) => s.value === updateForm.status,
//   );
//   const currentSubStatuses = subStatusMap[updateForm.status] || [];

//   const isStatusTerminal =
//     currentStatus?.statusId === "CLOSED" ||
//     currentStatus?.requiresClosure ||
//     currentStatus?.isTerminal ||
//     false;

//   const getUserDisplayName = (userId) => {
//     if (!userId) return "N/A";
//     if (userNamesCache[userId]) {
//       return userNamesCache[userId];
//     }
//     return userId;
//   };

//   return (
//     <>
//       <Modal
//         show={show}
//         onHide={onClose}
//         size="xl"
//         dialogClassName="custom-modal-dialog-TEM"
//         contentClassName="custom-modal-content-TEM"
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>Edit Ticket {ticket.ticketId}</Modal.Title>
//           <Button
//             variant="outline-info"
//             size="sm"
//             onClick={refreshAllData}
//             disabled={refreshing}
//             style={{ marginLeft: "auto", marginRight: "10px" }}
//           >
//             {refreshing ? (
//               <>
//                 <Spinner animation="border" size="sm" className="me-1" />
//                 Refreshing...
//               </>
//             ) : (
//               <>🔄 Refresh Data</>
//             )}
//           </Button>
//         </Modal.Header>

//         <Modal.Body>
//           <Row>
//             {/* LEFT COLUMN - Edit Form */}
//             <Col md={6} className="border-end">
//               <div className="pe-3">
//                 <h6 className="mb-3 text-primary">Ticket Information</h6>

//                 {/* Loading Indicators */}
//                 {(loadingAssignedTo || loadingStatuses) && (
//                   <div className="text-center py-3 mb-3 alert alert-info">
//                     <Spinner animation="border" size="sm" />
//                     <span className="ms-2">
//                       {loadingStatuses
//                         ? "Loading status options..."
//                         : "Loading assignee options..."}
//                     </span>
//                   </div>
//                 )}

//                 {/* Ticket Name */}
//                 <Form.Group className="mb-3">
//                   <Form.Label>Ticket Name</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={updateForm.ticketName}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         ticketName: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>

//                 {/* Submitted By */}
//                 <Form.Group className="mb-3">
//                   <Form.Label>Submitted By</Form.Label>
//                   {loadingSubmittedBy ? (
//                     <div className="text-center py-2">
//                       <Spinner animation="border" size="sm" />
//                     </div>
//                   ) : (
//                     <Select
//                       options={submittedByOptions}
//                       value={
//                         submittedByOptions.find(
//                           (opt) => opt.value === updateForm.submittedBy,
//                         ) || null
//                       }
//                       onChange={handleSubmittedByChange}
//                       placeholder="Select submitter..."
//                       isClearable
//                     />
//                   )}
//                 </Form.Group>

//                 {/* Ticket Type, Reason, Category */}
//                 <Row>
//                   <Col md={12} className="mb-3">
//                     <Form.Label>Ticket Type</Form.Label>
//                     <Select
//                       options={typeOptions}
//                       value={typeOptions.find(
//                         (opt) => opt.value === updateForm.ticketType,
//                       )}
//                       onChange={handleTypeChange}
//                       placeholder="Select Type"
//                     />
//                   </Col>
//                   <Col md={12} className="mb-3">
//                     <Form.Label>Ticket Reason</Form.Label>
//                     <Select
//                       options={reasonOptions}
//                       value={reasonOptions.find(
//                         (opt) => opt.value === updateForm.ticketReason,
//                       )}
//                       onChange={handleReasonChange}
//                       placeholder="Select Reason"
//                       isDisabled={!updateForm.ticketType}
//                     />
//                   </Col>
//                   <Col md={12} className="mb-3">
//                     <Form.Label>Category</Form.Label>
//                     <Select
//                       options={categoryOptions}
//                       value={categoryOptions.find(
//                         (opt) => opt.value === updateForm.category,
//                       )}
//                       onChange={handleCategoryChange}
//                       placeholder="Select Category"
//                       isDisabled={!updateForm.ticketReason}
//                     />
//                   </Col>
//                 </Row>

//                 {/* Priority and Due Date */}
//                 <Row>
//                   <Col md={6} className="mb-3">
//                     <Form.Label>Priority</Form.Label>
//                     <Form.Select
//                       value={updateForm.priority}
//                       onChange={(e) =>
//                         setUpdateForm({
//                           ...updateForm,
//                           priority: e.target.value,
//                         })
//                       }
//                     >
//                       <option value="Low">Low</option>
//                       <option value="Normal">Normal</option>
//                       <option value="Medium">Medium</option>
//                       <option value="High">High</option>
//                       <option value="Critical">Critical</option>
//                     </Form.Select>
//                   </Col>
//                   <Col md={6} className="mb-3">
//                     <Form.Label>Due Date</Form.Label>
//                     <Form.Control
//                       type="date"
//                       required
//                       value={updateForm.dueDate}
//                       onChange={(e) =>
//                         setUpdateForm({
//                           ...updateForm,
//                           dueDate: e.target.value,
//                         })
//                       }
//                     />
//                   </Col>
//                 </Row>

//                 {/* Project Assigned */}
//                 <Form.Group className="mb-3">
//                   <Form.Label>Project Assigned</Form.Label>
//                   {loadingProjects ? (
//                     <div className="text-center py-2">
//                       <Spinner animation="border" size="sm" />
//                       <span className="ms-2">Loading projects...</span>
//                     </div>
//                   ) : isProjectReadOnly ? (
//                     <Form.Control
//                       type="text"
//                       value={
//                         currentProject
//                           ? `${currentProject.prTitle} (${currentProject.projectId})`
//                           : updateForm.projectAssigned
//                       }
//                       disabled
//                       className="bg-light"
//                     />
//                   ) : (
//                     <>
//                       <Select
//                         isMulti
//                         options={projectOptionsBySubmitter}
//                         value={projectOptionsBySubmitter.filter((opt) =>
//                           updateForm.projectAssigned
//                             ?.split(",")
//                             .includes(opt.value),
//                         )}
//                         onChange={handleProjectChange}
//                         placeholder={
//                           updateForm.submittedBy
//                             ? "Select projects..."
//                             : "Please select Submitted By first"
//                         }
//                         isDisabled={!updateForm.submittedBy || loadingProjects}
//                         noOptionsMessage={() =>
//                           !updateForm.submittedBy
//                             ? "Please select Submitted By first"
//                             : "No projects available for this submitter"
//                         }
//                       />
//                       {updateForm.submittedBy &&
//                         projectOptionsBySubmitter.length > 0 && (
//                           <Form.Text className="text-muted">
//                             <Badge
//                               bg={
//                                 updateForm.projectAssigned
//                                   ? "primary"
//                                   : "secondary"
//                               }
//                               className="mt-1"
//                               style={{ fontSize: "0.8rem" }}
//                             >
//                               {getProjectCountText()}
//                             </Badge>
//                           </Form.Text>
//                         )}
//                     </>
//                   )}
//                 </Form.Group>

//                 {/* Assigned To */}
//                 <Form.Group className="mb-3">
//                   <Form.Label>Assigned To</Form.Label>
//                   {loadingAssignedTo ? (
//                     <div className="text-center py-2">
//                       <Spinner animation="border" size="sm" />
//                     </div>
//                   ) : (
//                     <Select
//                       isMulti
//                       options={assignedToOptions}
//                       value={assignedToOptions.filter((opt) =>
//                         updateForm.assignedTo.includes(opt.value),
//                       )}
//                       onChange={(selected) =>
//                         setUpdateForm({
//                           ...updateForm,
//                           assignedTo: selected
//                             ? selected.map((o) => o.value)
//                             : [],
//                         })
//                       }
//                       placeholder="Select assignees..."
//                     />
//                   )}
//                   <small className="text-muted">
//                     {assignedToOptions.length} assignee(s) available
//                   </small>
//                 </Form.Group>

//                 {/* Status and Sub Status */}
//                 <Row>
//                   <Col md={6} className="mb-3">
//                     <Form.Label>Status</Form.Label>
//                     <Select
//                       options={statusOptions}
//                       value={statusOptions.find(
//                         (opt) => opt.value === updateForm.status,
//                       )}
//                       onChange={handleStatusChange}
//                       placeholder="Select Status"
//                       isLoading={loadingStatuses}
//                       isDisabled={loadingStatuses}
//                     />
//                   </Col>
//                   <Col md={6} className="mb-3">
//                     <Form.Label>Sub Status</Form.Label>
//                     <Select
//                       options={currentSubStatuses}
//                       value={currentSubStatuses.find(
//                         (opt) => opt.value === updateForm.subStatus,
//                       )}
//                       onChange={handleSubStatusChange}
//                       placeholder="Select Sub Status"
//                       isDisabled={!updateForm.status || loadingStatuses}
//                     />
//                   </Col>
//                 </Row>

//                 {/* Description */}
//                 <Form.Group className="mb-3">
//                   <Form.Label>Description</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={4}
//                     value={updateForm.description}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         description: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>

//                 {/* Inline Closure Form - Shown only for terminal statuses */}
//                 {isStatusTerminal && (
//                   <div className="mt-4 p-3 border rounded bg-light">
//                     <h6 className="mb-3 text-danger">Close Ticket Details</h6>

//                     <Form.Group className="mb-3">
//                       <Form.Label>Closure Type *</Form.Label>
//                       <Select
//                         options={closureTypeOptions}
//                         value={closureTypeOptions.find(
//                           (o) => o.value === closureForm.closureTypeCode,
//                         )}
//                         onChange={(opt) =>
//                           setClosureForm({
//                             ...closureForm,
//                             closureTypeCode: opt?.value,
//                           })
//                         }
//                         placeholder="Select closure type"
//                       />
//                     </Form.Group>

//                     <Form.Group className="mb-3">
//                       <Form.Label>Closure Remarks *</Form.Label>
//                       <Form.Control
//                         as="textarea"
//                         rows={3}
//                         value={closureForm.closureRemarks}
//                         onChange={(e) =>
//                           setClosureForm({
//                             ...closureForm,
//                             closureRemarks: e.target.value,
//                           })
//                         }
//                         placeholder="Provide closure remarks..."
//                       />
//                     </Form.Group>

//                     <hr />
//                     <h6 className="mb-3">Efforts (Work Log)</h6>
//                     {closureForm.efforts.map((effort, index) => (
//                       <Row key={index} className="mb-3">
//                         <Col md={12} className="mb-2">
//                           <Form.Label>Worked By *</Form.Label>
//                           <Select
//                             options={workLogUserOptions}
//                             value={workLogUserOptions.find(
//                               (o) => o.value === effort.workedBy,
//                             )}
//                             onChange={(opt) => {
//                               const updated = [...closureForm.efforts];
//                               updated[index].workedBy = opt?.value || "";
//                               setClosureForm({
//                                 ...closureForm,
//                                 efforts: updated,
//                               });
//                             }}
//                             placeholder="Select user"
//                             isLoading={loadingWorkLogUsers}
//                             isDisabled={!updateForm.projectAssigned}
//                           />
//                         </Col>
//                         <Col md={6}>
//                           <Form.Label>Effort *</Form.Label>
//                           <Form.Control
//                             type="number"
//                             value={effort.effortValue}
//                             onChange={(e) => {
//                               const updated = [...closureForm.efforts];
//                               updated[index].effortValue = e.target.value;
//                               setClosureForm({
//                                 ...closureForm,
//                                 efforts: updated,
//                               });
//                             }}
//                             placeholder="Hours"
//                           />
//                         </Col>
//                         <Col md={4}>
//                           <Form.Label>Unit</Form.Label>
//                           <Form.Select
//                             value={effort.effortUnit}
//                             onChange={(e) => {
//                               const updated = [...closureForm.efforts];
//                               updated[index].effortUnit = e.target.value;
//                               setClosureForm({
//                                 ...closureForm,
//                                 efforts: updated,
//                               });
//                             }}
//                           >
//                             <option value="HOURS">Hours</option>
//                             <option value="DAYS">Days</option>
//                           </Form.Select>
//                         </Col>
//                         <Col md={2} className="d-flex align-items-end">
//                           <Button
//                             variant="danger"
//                             size="sm"
//                             onClick={() => {
//                               const updated = closureForm.efforts.filter(
//                                 (_, i) => i !== index,
//                               );
//                               setClosureForm({
//                                 ...closureForm,
//                                 efforts: updated,
//                               });
//                             }}
//                             disabled={closureForm.efforts.length === 1}
//                           >
//                             ✕
//                           </Button>
//                         </Col>
//                       </Row>
//                     ))}
//                     <Button
//                       variant="outline-primary"
//                       size="sm"
//                       onClick={() =>
//                         setClosureForm({
//                           ...closureForm,
//                           efforts: [
//                             ...closureForm.efforts,
//                             {
//                               workedBy: "",
//                               effortValue: "",
//                               effortUnit: "HOURS",
//                             },
//                           ],
//                         })
//                       }
//                       disabled={!updateForm.projectAssigned}
//                     >
//                       + Add Effort
//                     </Button>
//                   </div>
//                 )}
//               </div>
//             </Col>

//             {/* RIGHT COLUMN - Work Log History */}
//             <Col md={6}>
//               <div className="ps-3">
//                 <div className="d-flex justify-content-between align-items-center mb-3">
//                   <h6 className="mb-0 text-primary">Work Log History</h6>
//                   <Button
//                     variant="outline-info"
//                     size="sm"
//                     onClick={handleOpenWorkLogModal}
//                   >
//                     + Add Work Log
//                   </Button>
//                 </div>

//                 {loadingWorkLogs || refreshing ? (
//                   <div className="text-center py-5">
//                     <Spinner animation="border" size="sm" />
//                     <span className="ms-2">
//                       {refreshing
//                         ? "Refreshing data..."
//                         : "Loading work logs..."}
//                     </span>
//                   </div>
//                 ) : workLogsData?.workLogsByUser &&
//                   workLogsData.workLogsByUser.length > 0 ? (
//                   <>
//                     <div className="mb-3 p-2 bg-light rounded">
//                       <small className="text-muted">
//                         Total Work Logged:{" "}
//                         <strong>
//                           {workLogsData.summary?.totalWorkLogHours || 0} hours
//                         </strong>{" "}
//                         | Total Entries:{" "}
//                         <strong>
//                           {workLogsData.summary?.totalWorkLogs || 0}
//                         </strong>
//                       </small>
//                     </div>

//                     <div style={{ maxHeight: "500px", overflowY: "auto" }}>
//                       {workLogsData.workLogsByUser.map((userWork) => (
//                         <div
//                           key={userWork.userId}
//                           className="mb-3 border rounded p-2"
//                         >
//                           <div className="d-flex justify-content-between align-items-center mb-2">
//                             <h6 className="mb-0">
//                               <Badge bg="info" className="me-2">
//                                 {userWork.logCount} entry(s)
//                               </Badge>
//                               User:{" "}
//                               <strong>
//                                 {loadingUserNames ? (
//                                   <Spinner animation="border" size="sm" />
//                                 ) : (
//                                   getUserDisplayName(userWork.userId)
//                                 )}
//                               </strong>
//                             </h6>
//                             <Badge bg="primary">
//                               Total: {userWork.totalHours} hours
//                             </Badge>
//                           </div>

//                           <Table size="sm" bordered hover className="mb-0">
//                             <thead className="table-light">
//                               <tr>
//                                 <th>Date</th>
//                                 <th>Hours</th>
//                                 <th>Unit</th>
//                                 <th>Remarks</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {userWork.logs.map((log, idx) => (
//                                 <tr key={log.ticketLogGuid || idx}>
//                                   <td>
//                                     {log.changedDate &&
//                                     log.changedDate !== "0001-01-01T00:00:00"
//                                       ? moment(log.changedDate).format(
//                                           "DD-MMM-YYYY",
//                                         )
//                                       : moment(log.createdDate).format(
//                                           "DD-MMM-YYYY",
//                                         )}
//                                   </td>
//                                   <td>{log.effortValue}</td>
//                                   <td>{log.effortUnit || "HOURS"}</td>
//                                   <td
//                                     className="text-wrap"
//                                     style={{ maxWidth: "250px" }}
//                                   >
//                                     {log.closureRemarks || "-"}
//                                   </td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </Table>
//                         </div>
//                       ))}
//                     </div>
//                   </>
//                 ) : (
//                   <div className="text-center py-5 text-muted border rounded">
//                     <i>No work logs found for this ticket</i>
//                   </div>
//                 )}
//               </div>
//             </Col>
//           </Row>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={onClose}>
//             Cancel
//           </Button>
//           {isStatusTerminal ? (
//             <Button
//               variant="danger"
//               onClick={handleCloseTicket}
//               disabled={loading || isClosing}
//             >
//               {isClosing ? "Closing..." : "Close Ticket"}
//             </Button>
//           ) : (
//             <Button
//               variant="primary"
//               onClick={handleUpdateTicket}
//               disabled={loading}
//             >
//               {loading ? "Updating..." : "Update Ticket"}
//             </Button>
//           )}
//         </Modal.Footer>
//       </Modal>

//       {/* Work Log Modal */}
//       <Modal
//         show={showWorkLogModal}
//         onHide={() => setShowWorkLogModal(false)}
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>Add Work Log</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Worked By</Form.Label>
//               {loadingWorkLogUsers ? (
//                 <div className="text-center py-2">
//                   <Spinner animation="border" size="sm" />
//                   <span className="ms-2">Loading users...</span>
//                 </div>
//               ) : (
//                 <Select
//                   options={workLogUserOptions}
//                   value={workLogUserOptions.find(
//                     (o) => o.value === workLogForm.workedBy,
//                   )}
//                   onChange={(opt) =>
//                     setWorkLogForm({
//                       ...workLogForm,
//                       workedBy: opt?.value,
//                     })
//                   }
//                   placeholder={
//                     updateForm.projectAssigned
//                       ? "Select user..."
//                       : "Please select a project first"
//                   }
//                   isDisabled={
//                     !updateForm.projectAssigned || loadingWorkLogUsers
//                   }
//                   noOptionsMessage={() =>
//                     !updateForm.projectAssigned
//                       ? "Please select a project first"
//                       : "No users available for selected project(s)"
//                   }
//                 />
//               )}
//             </Form.Group>
//             <Row>
//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Effort (Hours)</Form.Label>
//                   <Form.Control
//                     type="number"
//                     value={workLogForm.effortValue}
//                     onChange={(e) =>
//                       setWorkLogForm({
//                         ...workLogForm,
//                         effortValue: e.target.value,
//                       })
//                     }
//                     placeholder="Enter effort hours"
//                   />
//                 </Form.Group>
//               </Col>
//               <Col md={6}>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Unit</Form.Label>
//                   <Form.Select
//                     value={workLogForm.effortUnit}
//                     onChange={(e) =>
//                       setWorkLogForm({
//                         ...workLogForm,
//                         effortUnit: e.target.value,
//                       })
//                     }
//                   >
//                     <option value="HOURS">Hours</option>
//                     <option value="DAYS">Days</option>
//                   </Form.Select>
//                 </Form.Group>
//               </Col>
//             </Row>
//             <Form.Group className="mb-3">
//               <Form.Label>Remarks</Form.Label>
//               <Form.Control
//                 as="textarea"
//                 rows={4}
//                 value={workLogForm.remarks}
//                 onChange={(e) =>
//                   setWorkLogForm({
//                     ...workLogForm,
//                     remarks: e.target.value,
//                   })
//                 }
//                 placeholder="Enter work log remarks..."
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => setShowWorkLogModal(false)}
//           >
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             onClick={handleAddWorkLog}
//             disabled={
//               !updateForm.projectAssigned ||
//               !workLogForm.workedBy ||
//               !workLogForm.effortValue
//             }
//           >
//             Save Work Log
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// };

// export default TicketEditModal;
